/**
 * Inbound sync — the other half of the queue in ./queue.ts.
 *
 * The phone walks each resource in turn — templates, its own sessions,
 * reviewer outcomes on them, and the captures and answers hanging off them —
 * each with its own watermark, and applies what comes back under two
 * invariants from spec §6:
 *
 *   1. Never overwrite a newer local row with an older server row.
 *   2. Never stomp a row that still has unsent work in the queue.
 *
 * Both are enforced in SQL rather than in JavaScript, so a row arriving while
 * a local write is in flight can't slip through a read-then-write gap.
 */
import { z } from 'zod';
import {
  PullPage,
  ServerCaptureRow,
  ServerFieldValueRow,
  ServerProjectRow,
  ServerSessionRow,
  ServerTemplateRow,
  type PullResource,
} from '@kapture/types';
import { all, run } from '../db/sqlite';
import { platform } from '../platform';

/** Watermark for a resource that has never been pulled (spec §6). */
const EPOCH = '1970-01-01T00:00:00Z';

/** Guard against a runaway server that always returns a next_cursor. */
const MAX_PAGES = 100;

interface ResourceSpec {
  resource: PullResource;
  path: string;
  apply: (rows: unknown[]) => Promise<void>;
  /**
   * This resource answers with the current set, not a change feed, and its
   * `apply` replaces the local table wholesale.
   *
   * Such an apply can only ever see one page: a second page would arrive after
   * the first had already been swept, and delete it. The flag is what makes
   * `pullResource` refuse to follow a cursor here rather than leaving that as
   * an invariant nobody wrote down — a server that started paginating would
   * otherwise quietly empty the table on every pull.
   */
  snapshot?: true;
}

/* ------------------------------- watermarks ------------------------------- */

export async function readWatermark(resource: PullResource): Promise<string | null> {
  const rows = await all<{ cursor: string }>(
    `SELECT cursor FROM sync_watermarks WHERE resource = ?`,
    [resource],
  );
  return rows[0]?.cursor ?? null;
}

async function writeWatermark(resource: PullResource, cursor: string): Promise<void> {
  await run(
    `INSERT INTO sync_watermarks (resource, cursor, updated_at)
     VALUES (?, ?, ?)
     ON CONFLICT(resource) DO UPDATE SET
       cursor = excluded.cursor,
       updated_at = excluded.updated_at`,
    [resource, cursor, new Date().toISOString()],
  );
}

/* --------------------------------- applying -------------------------------- */

/**
 * Rows with pending work are left alone until the queue drains. Pull is
 * eventually consistent, and Sarah's unsent edit outranks the server's older
 * copy of it. Takes one positional bind: the row id.
 */
const NOT_QUEUED = `NOT EXISTS (
  SELECT 1 FROM sync_tasks
   WHERE sync_tasks.entity_id = ?
     AND sync_tasks.status IN ('queued','uploading','retrying','conflict')
)`;

async function applyDelete(table: string, id: string, version: number): Promise<void> {
  await run(`DELETE FROM ${table} WHERE id = ? AND version <= ? AND ${NOT_QUEUED}`, [
    id,
    version,
    id,
  ]);
}

async function applySession(raw: unknown): Promise<void> {
  const row = ServerSessionRow.parse(raw);
  if (row.deleted_at) {
    await applyDelete('sessions', row.id, row.version);
    return;
  }
  await run(
    `INSERT INTO sessions
       (id, title, template_id, project_id, status, owner_id, reviewer_id, created_at,
        updated_at, submitted_at, approved_at, meta, start_location_json, review_note,
        flagged_fields_json, version, org_id, server_updated_at)
     SELECT ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?
      WHERE ${NOT_QUEUED}
     ON CONFLICT(id) DO UPDATE SET
       title = excluded.title,
       template_id = excluded.template_id,
       -- Assigned, not coalesced. A manager filing a session under a project
       -- in the console, or moving it out again, is the server having an
       -- opinion — and "no project" is one of the two things they can say.
       project_id = excluded.project_id,
       status = excluded.status,
       owner_id = excluded.owner_id,
       reviewer_id = excluded.reviewer_id,
       updated_at = excluded.updated_at,
       submitted_at = excluded.submitted_at,
       approved_at = excluded.approved_at,
       meta = excluded.meta,
       -- Never overwrite a start point with nothing. The server treats this
       -- as set-once, so a null here means "this row carries no opinion",
       -- not "clear it" — and a fix the phone recorded before the column
       -- existed server-side would otherwise be wiped by the next pull.
       start_location_json = COALESCE(excluded.start_location_json, sessions.start_location_json),
       -- Assigned, not coalesced. The server clears this on resubmission, and
       -- a null there means "cleared" rather than "no opinion" — coalescing
       -- would leave the inspector staring at a complaint about work they
       -- have already redone.
       review_note = excluded.review_note,
       -- Assigned like the note it belongs to: the server clears both on
       -- resubmission, so an empty list means "cleared", not "no opinion".
       flagged_fields_json = excluded.flagged_fields_json,
       version = excluded.version,
       org_id = excluded.org_id,
       server_updated_at = excluded.server_updated_at
     WHERE excluded.version > sessions.version`,
    [
      row.id,
      row.title,
      row.template_id,
      row.project_id,
      row.status,
      row.owner_id,
      row.reviewer_id,
      row.created_at,
      row.updated_at,
      row.submitted_at,
      row.approved_at,
      JSON.stringify(row.meta ?? {}),
      row.start_location ? JSON.stringify(row.start_location) : null,
      row.review_note,
      JSON.stringify(row.flagged_fields ?? []),
      row.version,
      row.org_id,
      row.updated_at,
      row.id, // NOT_QUEUED guard
    ],
  );
}

async function applyCapture(raw: unknown): Promise<void> {
  const row = ServerCaptureRow.parse(raw);
  if (row.deleted_at) {
    await applyDelete('captures', row.id, row.version);
    return;
  }
  await run(
    `INSERT INTO captures
       (id, session_id, kind, byte_size, gps_json, tags_json, ai_fields_json,
        linked_field_id, payload_json, captured_at, ai_status, ai_detail,
        created_at, updated_at, version, org_id, server_updated_at, media_id)
     SELECT ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?
      WHERE ${NOT_QUEUED}
     ON CONFLICT(id) DO UPDATE SET
       kind = excluded.kind,
       byte_size = excluded.byte_size,
       gps_json = excluded.gps_json,
       tags_json = excluded.tags_json,
       ai_fields_json = excluded.ai_fields_json,
       ai_status = excluded.ai_status,
       ai_detail = excluded.ai_detail,
       linked_field_id = excluded.linked_field_id,
       payload_json = excluded.payload_json,
       -- COALESCE, not a plain assignment. A server that predates the column,
       -- or a row no device ever timed, sends null, and null here must not
       -- overwrite a capture time this device recorded itself. Note also what
       -- is absent from this list: created_at is set on insert and never
       -- updated, so a row created here keeps its local clock instead of
       -- adopting the server's arrival stamp.
       captured_at = COALESCE(excluded.captured_at, captures.captured_at),
       updated_at = excluded.updated_at,
       version = excluded.version,
       org_id = excluded.org_id,
       server_updated_at = excluded.server_updated_at,
       media_id = excluded.media_id
     WHERE excluded.version > captures.version`,
    [
      row.id,
      row.session_id,
      row.kind,
      row.byte_size,
      row.gps ? JSON.stringify(row.gps) : null,
      JSON.stringify(row.tags ?? []),
      JSON.stringify(row.ai_fields ?? {}),
      row.linked_field_id,
      JSON.stringify(row.payload ?? {}),
      row.captured_at,
      row.ai_status,
      row.ai_detail,
      row.created_at,
      row.updated_at,
      row.version,
      row.org_id,
      row.updated_at,
      row.media_id,
      row.id, // NOT_QUEUED guard
    ],
  );
}

async function applyFieldValue(raw: unknown): Promise<void> {
  const row = ServerFieldValueRow.parse(raw);
  // The queue keys a field value by "{sessionId}:{fieldId}" — the row has no
  // id of its own — so the guard has to be given that composite, not a plain
  // id like the other resources.
  const entityId = `${row.session_id}:${row.field_id}`;

  if (row.deleted_at) {
    await run(
      `DELETE FROM field_values
        WHERE session_id = ? AND field_id = ? AND version <= ? AND ${NOT_QUEUED}`,
      [row.session_id, row.field_id, row.version, entityId],
    );
    return;
  }

  await run(
    `INSERT INTO field_values
       (session_id, field_id, value_json, source, confidence, source_capture_id,
        updated_at, version, org_id)
     SELECT ?, ?, ?, ?, ?, ?, ?, ?, ?
      WHERE ${NOT_QUEUED}
     ON CONFLICT(session_id, field_id) DO UPDATE SET
       value_json = excluded.value_json,
       source = excluded.source,
       confidence = excluded.confidence,
       source_capture_id = excluded.source_capture_id,
       updated_at = excluded.updated_at,
       version = excluded.version,
       org_id = excluded.org_id
     WHERE excluded.version > field_values.version`,
    [
      row.session_id,
      row.field_id,
      JSON.stringify(row.value ?? null),
      row.source,
      row.confidence,
      row.source_capture_id,
      row.updated_at,
      row.version,
      row.org_id,
      entityId, // NOT_QUEUED guard
    ],
  );
}

async function applyTemplate(raw: unknown): Promise<void> {
  const row = ServerTemplateRow.parse(raw);
  if (row.deleted_at) {
    await applyDelete('templates', row.id, row.version);
    return;
  }
  // Templates are read-only on the phone, so there is no queue guard here —
  // nothing local can be lost.
  await run(
    `INSERT INTO templates
       (id, org_id, name, definition, version, updated_at, server_updated_at)
     VALUES (?, ?, ?, ?, ?, ?, ?)
     ON CONFLICT(id) DO UPDATE SET
       name = excluded.name,
       definition = excluded.definition,
       version = excluded.version,
       updated_at = excluded.updated_at,
       server_updated_at = excluded.server_updated_at
     WHERE excluded.version > templates.version`,
    [
      row.id,
      row.org_id,
      row.name,
      JSON.stringify(row.definition),
      row.version,
      row.updated_at,
      row.updated_at,
    ],
  );
}

/**
 * Replace the local project list with what the server just sent.
 *
 * Unlike every other resource here this is not a row-by-row merge, because the
 * question the endpoint answers is not "what changed" but "which projects are
 * mine now". An unassignment removes a project from that answer without ever
 * producing a row to merge, so a merge-only apply would leave a project on the
 * phone forever after someone took it off.
 *
 * Rows that fail to parse are skipped rather than failing the page — same rule
 * as `applyEach` — but a page that parses to nothing at all is still applied:
 * "you are on no projects" is a real answer, and refusing to act on it would
 * be the same bug in a different place.
 */
async function applyProjects(rows: unknown[]): Promise<void> {
  const parsed: ServerProjectRow[] = [];
  for (const raw of rows) {
    const result = ServerProjectRow.safeParse(raw);
    if (result.success) parsed.push(result.data);
    else console.warn('[pull] skipped unparseable project row', result.error.issues);
  }

  const syncedAt = new Date().toISOString();
  for (const row of parsed) {
    await run(
      `INSERT INTO projects
         (id, slug, name, code, status, default_template_id, default_template_name,
          my_session_count, last_worked_at, synced_at)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?)
       ON CONFLICT(id) DO UPDATE SET
         slug = excluded.slug,
         name = excluded.name,
         code = excluded.code,
         status = excluded.status,
         default_template_id = excluded.default_template_id,
         default_template_name = excluded.default_template_name,
         my_session_count = excluded.my_session_count,
         last_worked_at = excluded.last_worked_at,
         synced_at = excluded.synced_at`,
      [
        row.id,
        row.slug,
        row.name,
        row.code,
        row.status,
        row.default_template_id,
        row.default_template_name,
        row.my_session_count,
        row.last_worked_at,
        syncedAt,
      ],
    );
  }

  // The sweep half of the replace: anything not in this page is no longer
  // assigned. Keyed on `synced_at` rather than an id list so the statement
  // does not grow with the number of projects. Safe only because this
  // resource is a `snapshot` — see `ResourceSpec`.
  await run(`DELETE FROM projects WHERE synced_at <> ?`, [syncedAt]);
}

/**
 * Apply a page one row at a time. A row the client can't parse is skipped and
 * logged rather than failing the page: one malformed row shouldn't strand
 * every good row behind it forever. Any other failure propagates, which
 * leaves the watermark where it was.
 */
function applyEach(apply: (raw: unknown) => Promise<void>) {
  return async (rows: unknown[]): Promise<void> => {
    for (const raw of rows) {
      try {
        await apply(raw);
      } catch (err) {
        if (err instanceof z.ZodError) {
          console.warn('[pull] skipped unparseable row', err.issues);
          continue;
        }
        throw err;
      }
    }
  };
}

/**
 * Apply one row fetched outside the paged pull — the single-row endpoints
 * behind a deep link or a keep-remote conflict resolution.
 *
 * Note this goes through the same queue guard, so resolving keep-remote must
 * clear the task first or the write is a no-op.
 */
export async function applyServerRow(
  kind: 'session' | 'capture',
  raw: unknown,
): Promise<void> {
  await (kind === 'session' ? applySession(raw) : applyCapture(raw));
}

const RESOURCES: ResourceSpec[] = [
  { resource: 'templates', path: '/templates', apply: applyEach(applyTemplate) },
  // Before the sessions, so a session arriving with a `project_id` has
  // something to name. Not `applyEach`: see `applyProjects`.
  {
    resource: 'projects_mine',
    path: '/projects/mine',
    apply: applyProjects,
    snapshot: true,
  },
  { resource: 'sessions_mine', path: '/sessions/mine', apply: applyEach(applySession) },
  {
    resource: 'sessions_reviewed',
    path: '/sessions/mine/reviewed',
    apply: applyEach(applySession),
  },
  // After the sessions, so a capture never lands before the row its foreign
  // key points at. This is how `ai_fields` reaches the device: the extraction
  // worker writes suggestions server-side, and without this resource the
  // phone synced the session but never the captures hanging off it, so the
  // inspector was never offered anything to accept.
  {
    // A reviewer sees other people's submissions. The server has answered
    // `/sessions/review-queue` since the review loop shipped; nothing pulled
    // it, so the phone's inbox listed only what this device had submitted and
    // called itself a single-user stub.
    //
    // Harmless for an inspector: the endpoint scopes to the caller's own
    // queue, so a non-reviewer simply pulls an empty page.
    resource: 'sessions_review_queue',
    path: '/sessions/review-queue',
    apply: applyEach(applySession),
  },
  { resource: 'captures_mine', path: '/captures/mine', apply: applyEach(applyCapture) },
  // Also after the sessions, for the same foreign key reason. Without this a
  // reinstall showed a filled-in form as untouched.
  {
    resource: 'field_values_mine',
    path: '/field-values/mine',
    apply: applyEach(applyFieldValue),
  },
];

/* ---------------------------------- pull ---------------------------------- */

/**
 * Envelope only. Rows stay `unknown` here and are validated per-resource in
 * `apply`, so one bad row can be skipped instead of invalidating the page.
 */
const RawPage = PullPage(z.unknown());

async function pullResource(spec: ResourceSpec): Promise<void> {
  let cursor = (await readWatermark(spec.resource)) ?? EPOCH;
  let serverTime: string | null = null;
  let drained = false;

  for (let page = 0; page < MAX_PAGES; page++) {
    const parsed = RawPage.parse(
      await platform().api.get<unknown>(
        `${spec.path}?updated_since=${encodeURIComponent(cursor)}`,
      ),
    );
    await spec.apply(parsed.items);
    serverTime = parsed.server_time;

    if (!parsed.next_cursor) {
      drained = true;
      break;
    }
    if (spec.snapshot) {
      // A snapshot apply has just replaced the local table with this page.
      // Fetching the next one would replace it again with the remainder,
      // deleting everything this page delivered. Stop, and say so: the
      // resource and the endpoint disagree, and that is a bug to fix rather
      // than a condition to handle.
      console.warn(
        `[sync] ${spec.resource}: snapshot resource returned a next_cursor; ignoring the rest of the pages`,
      );
      drained = true;
      break;
    }
    cursor = parsed.next_cursor;
  }

  // Written only after every page applied cleanly. Advancing past a window we
  // failed to apply would skip those rows permanently.
  if (!serverTime) return;

  if (drained) {
    await writeWatermark(spec.resource, serverTime);
    return;
  }

  // We stopped because MAX_PAGES ran out, not because the server ran out of
  // rows. Writing `server_time` here would move the watermark past everything
  // the remaining pages were going to deliver — and because `updated_since` is
  // a strict `>`, those rows would never be offered again. Resume from the
  // last cursor we actually consumed instead, so the next pull continues
  // exactly where this one stopped.
  console.warn(
    `[sync] ${spec.resource}: stopped at the ${MAX_PAGES}-page ceiling with more to fetch; resuming from ${cursor} next pull`,
  );
  await writeWatermark(spec.resource, cursor);
}

let pullInFlight: Promise<void> | null = null;

async function runPull(): Promise<void> {
  for (const spec of RESOURCES) {
    try {
      await pullResource(spec);
    } catch (err) {
      // One resource failing shouldn't block the others. Its watermark stays
      // put, so the next pull retries exactly the window it missed.
      console.warn(`[pull] ${spec.resource} failed`, err);
    }
  }
}

/**
 * Pull every resource once. Concurrent callers share a single pass — the four
 * triggers firing together should produce one pull, not four.
 */
export function pullAll(): Promise<void> {
  pullInFlight ??= runPull().finally(() => {
    pullInFlight = null;
  });
  return pullInFlight;
}
